import { Button, Grid, Stack } from '@mui/material';
import { SimpleForm } from 'components/forms/SimpleForm';
import { MultiSelectInput } from 'components/inputs/MultiSelectInput';
import { MultilineTextInput } from 'components/inputs/MultilineTextInput';
import { SelectInput } from 'components/inputs/SelectInput';
import { TextInput } from 'components/inputs/TextInput';
import Panel from 'components/panels/Panel';
import * as yup from 'yup';
import { EventFormValues, SEVERITY_OPTIONS, SOURCE_OPTIONS, TYPE_OPTIONS } from './TestDataGenerator';

type EventsFormProps = {
  defaultValues: EventFormValues;
  validationSchema: yup.ObjectSchema<EventFormValues>;
  generateSingleEvent: (data: EventFormValues) => void;
  generateMultipleEvents: (data: EventFormValues) => void;
  generateLiveEvents: (data: EventFormValues) => void;
};

export const EventsForm = ({
  defaultValues,
  validationSchema,
  generateSingleEvent,
  generateMultipleEvents,
  generateLiveEvents,
}: EventsFormProps) => {
  return (
    <SimpleForm
      defaultValues={defaultValues}
      validationSchema={validationSchema}
      showButtons={false}
      onSubmit={() => {}}
    >
      {({ control, handleSubmit, formState, setValue, watch }) => {
        return (
          <Grid container spacing={3} sx={{ pr: 3, mt: 2 }}>
            <Grid item xs={12}>
              <Panel title="Event configuration">
                <Grid container item spacing={3}>
                  <Grid item xs={12}>
                    <TextInput
                      control={control}
                      name="unitId"
                      label="Asset external identifier(s) *"
                      gridSize={12}
                      placeholder="GREENER_001, GREENER_002, ..."
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <MultiSelectInput
                      title="Severity(ies) *"
                      options={SEVERITY_OPTIONS.map((option) => ({ id: option, title: option }))}
                      selectedOptions={watch('severity')}
                      onChange={(value) => setValue('severity', value, { shouldValidate: true })}
                      sx={{ width: '100%' }}
                      isError={!!formState.errors.severity}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <MultiSelectInput
                      title="Type(s) *"
                      options={TYPE_OPTIONS.map((option) => ({ id: option, title: option }))}
                      selectedOptions={watch('type')}
                      onChange={(value) => setValue('type', value, { shouldValidate: true })}
                      sx={{ width: '100%' }}
                      isError={!!formState.errors.type}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <SelectInput
                      control={control}
                      name="source"
                      label="Source"
                      gridSize={12}
                      options={SOURCE_OPTIONS}
                      getOptionLabel={(option) => option}
                      addNoneOption={false}
                    />
                  </Grid>
                  <TextInput control={control} name="code" label="Code(s) *" placeholder="123, 44, ..." />
                  <MultilineTextInput control={control} name="message" label="Message" rows={2} gridSize={12} />
                </Grid>
              </Panel>
            </Grid>
            <Grid item xs={12} md={4}>
              <Panel title="Single event">
                <Stack direction="column" gap={2}>
                  <Button variant="outlined" onClick={handleSubmit(generateSingleEvent)} disabled={!formState.isValid}>
                    Generate single event
                  </Button>
                </Stack>
              </Panel>
            </Grid>
            <Grid container item xs={12} md={4}>
              <Panel title="Multiple events">
                <Stack direction="column" gap={2}>
                  <TextInput control={control} name="period" label="Period *" gridSize={12} />
                  <TextInput control={control} name="from" label="From *" gridSize={12} />
                  <TextInput control={control} name="to" label="To *" gridSize={12} />
                  <Button
                    variant="outlined"
                    onClick={handleSubmit(generateMultipleEvents)}
                    disabled={!formState.isValid}
                  >
                    Generate multiple events
                  </Button>
                </Stack>
              </Panel>
            </Grid>
            <Grid item xs={12} md={4}>
              <Panel title="Live events">
                <Stack direction="column" gap={2}>
                  <TextInput control={control} name="period" label="Period *" gridSize={12} />
                  <TextInput control={control} name="duration" label="Duration *" gridSize={12} />
                  <Button variant="outlined" onClick={handleSubmit(generateLiveEvents)} disabled={!formState.isValid}>
                    Generate live events
                  </Button>
                </Stack>
              </Panel>
            </Grid>
          </Grid>
        );
      }}
    </SimpleForm>
  );
};
